import { useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';

import type { Options as PlyrOptions } from 'plyr';

const defaultControls = [
  'play',
  'rewind',
  'fast-forward',
  'progress',
  'current-time',
  'mute',
  'volume',
  'settings'
];

interface Props {
  onReady?: () => void;
  style?: React.CSSProperties;
  url: string;
}

const Audio = ({ onReady, style, url }: Props) => {
  const audioElement = useRef<HTMLAudioElement>(undefined);
  const player = useRef<Plyr>(undefined);

  const { messages, theme } = useReduxState();

  useEffect(() => {
    initPlyr();

    return () => {
      if (player.current) player.current.destroy();
    };
  }, []);

  const initPlyr = async () => {
    const Plyr = (await import('plyr')).default;

    const options: PlyrOptions = {
      controls: defaultControls,
      i18n: {
        normal: messages.commonNormal,
        speed: messages.videoSpeed
      }
    };
    player.current = new Plyr(audioElement.current, options);

    // event listeners
    if (onReady) player.current.on('ready', () => onReady());
  };

  return (
    <div className="audio" style={style}>
      {url ? (
        <audio className="audio-plyr" controls={true} ref={audioElement}>
          <source src={url} type="audio/mp3" />
        </audio>
      ) : null}

      <style jsx>{`
        .audio {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .audio-plyr {
          max-width: 100%;
        }
        .audio :global(.plyr) {
          border-radius: ${theme.cardBorderRadius};
        }
        .audio :global(input[type='range']:active::-webkit-slider-thumb),
        .audio :global(input[type='range']:active::-moz-range-thumb),
        .audio :global(input[type='range']:active::-ms-thumb) {
          background: ${theme.mainColor};
        }
        .audio :global(.plyr--audio .plyr__controls .plyr__control:hover),
        .audio
          :global(.plyr--audio .plyr__controls .plyr__control.plyr__tab-focus),
        .audio
          :global(
            .plyr--audio .plyr__controls .plyr__control[aria-expanded='true']
          ),
        .audio :global(.plyr__menu__container) {
          z-index: 110;
        }
        .audio :global(.plyr--audio .plyr__control.plyr__tab-focus),
        .audio :global(.plyr--audio .plyr__control[aria-expanded='true']),
        .audio
          :global(
            .plyr__menu__container
              label.plyr__control
              input[type='radio']:checked
              + span
          ),
        .audio
          :global(
            .plyr__menu__container
              .plyr__control[role='menuitemradio'][aria-checked='true']::before
          ),
        .audio :global(.plyr__control--overlaid) {
          background: ${theme.mainColor};
        }
        .audio :global(.plyr--audio .plyr__control) {
          border-radius: ${theme.buttonBorderRadius};
        }
        .audio :global(.plyr--audio .plyr__control:hover) {
          background: ${theme.mainColorActive};
        }
        .audio :global(.plyr__progress input[type='range']),
        .audio :global(.plyr__volume input[type='range']) {
          color: ${theme.mainColorActive};
        }
      `}</style>
    </div>
  );
};

export default Audio;
